import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from 'native-base';
import {
  ImageBackground, View, TouchableOpacity, Text, Pressable, ActivityIndicator
} from 'react-native';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useConnectionState,
  useLocalParticipant,
  useTracks,
} from "@livekit/components-react";
import { Character, PuzzleThemeCharacter, } from '../data/types';
import { ConnectionState } from 'livekit-client';
import { supabase } from '../lib/supabase';
import { useDispatch } from 'react-redux';
import { PayAPI } from '../data/payApi';

const Assistant = ({
  name,
  onDisconnect,
  playing,
  updateBg
}:{
  name: string;
  onDisconnect: (state?: string) => void;
  playing: number;
  updateBg: () => void;
}) => {
  const dispatch = useDispatch();
  const roomState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  // const tracks = useTracks();
  const [timeText, setTimeText] = useState("00:00");
  const timeInterval = useRef<NodeJS.Timeout>(undefined);

  const createTimer = ()=>{
    clearInterval(timeInterval.current);
    let time_num = 0;
    timeInterval.current = setInterval(()=>{
      // if(time_num % 10 === 0 && time_num > 0){
      //   updateBg();
      // }
      if(time_num % 10 === 0){ // 60 === 300 coins
        PayAPI.modifyCoins(dispatch, -50).then((e)=>{
          if(e === "not-coins"){
            clearInterval(timeInterval.current);
            onDisconnect("error");
          }
        })
      }
      time_num++;
      let min = Math.floor(time_num / 60);
      let sec = time_num % 60;
      let min_str = min < 10 ? `0${min}` : `${min}`;
      let sec_str = sec < 10 ? `0${sec}` : `${sec}`;
      setTimeText(`${min_str}:${sec_str}`);
    }, 1000);
  };

  useEffect(() => {
    if (roomState === ConnectionState.Connected) {
      localParticipant.setMicrophoneEnabled(true);
    }
  }, [localParticipant, roomState]);

  useEffect(()=>{
    if(playing === 1) createTimer();
    return ()=>{
      clearInterval(timeInterval.current);
    };
  }, [playing]);

  useEffect(()=>{
    return ()=>{
      clearInterval(timeInterval.current);
    };
  }, []);

  return (
    <View style={{
      flex: 1, padding: 20, width: "100%", height: '100%',
      backgroundColor: "rgba(0,0,0,0.5)",
      transitionDuration: "500ms",
      transitionTimingFunction: "ease-in-out",
    }}>
      <Box flex={1}>
        <Box style={{
          alignItems: "center", gap: 10, paddingTop: 30
        }}>
          <Text style={{ color: "#FFFFFF", fontSize: 24, fontWeight: "bold" }}>
            {name}
          </Text>
          <Text style={{ color: "#FFFFFF", fontSize: 16, fontWeight: "400" }}>
            {timeText}
          </Text>
          {playing === 0 && (
            <Box style={{
              paddingVertical: 20, alignItems: 'center',
            }}>
              <ActivityIndicator size="large" color="#FFFFFF" />
            </Box>
          )}
        </Box>
      </Box>
      <Box style={{ padding: 10, alignItems: "center" }}>
      <Pressable onPress={()=> {
        clearInterval(timeInterval.current);
        onDisconnect()
      } } style={{
        width: 60, height: 60, borderRadius: 30,
        justifyContent: "center", alignItems: "center",
        backgroundColor: "rgb(200,0,0)",
      }}>
        <FontAwesome6 name="phone" size={24} color="#FFFFFF"
          style={{
            transform: [{ rotate: "135deg" }]
          }} />
      </Pressable>
      </Box>
    </View>
  );
}

const PhoneScreen = ({
  character,
  onHangUp
}: {
  character: PuzzleThemeCharacter
  onHangUp: (state?: string) => void
} ) => {
  const [connectionDetails, setConnectionDetails] = useState<{
    wsUrl?: string;
    token?: string;
    shouldConnect: boolean;
  }>({ shouldConnect: false });
  const [playing, setPlaying] = useState(0); // 0 loading, 1 playing, 2 pause, 3 stop
  const [checkImage, setCheckImage] = useState(character.role_img_url || "");
  const indexRef = useRef(0);
  const phoneStartTime = useRef(0);

  useEffect(() => {
    if (character) initializeChat();
  }, [character]);

  const updateBgHandle = ()=>{
    let list: string[] = [];
    // let list = character.albums?.closeup || [];
    if(indexRef.current >= list.length){
      indexRef.current = 0;
    }else{
      indexRef.current++;
    }
    setCheckImage(list[indexRef.current] || "");
  }

  const initializeChat = async () => {
    connect();
  };

  const connect = useCallback(async () => {
    let token = "";
    let url = "";
    const NEXT_PUBLIC_LIVEKIT_URL = "wss://my3rdapp-1zjkgblv.livekit.cloud";
    // if (!process.env.NEXT_PUBLIC_LIVEKIT_URL) {
    //   throw new Error("NEXT_PUBLIC_LIVEKIT_URL is not set");
    // }
    url = NEXT_PUBLIC_LIVEKIT_URL;
    
    const { data: user } = await supabase.auth.getUser();
    const {
      accessToken
    } = await fetch("https://storyloom.ai/ai_girl/api/token",{
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Headers': 'Content-Type, Authorization',
        "user-token": user.user?.id || ""
      }
    }).then((res) => res.json());
    token = accessToken;
    setConnectionDetails({ wsUrl: url, token, shouldConnect: true });
  }, []);

  const disconnect = (state?: string)=>{
    setConnectionDetails((prev) => ({ ...prev, shouldConnect: false }));
    let time = Date.now() - phoneStartTime.current;
    let second = Math.floor(time / 1000);
    PayAPI.modifyPhoneTime(second);
    
    setPlaying(2);
    onHangUp(state);
  }
  
  return (
    <Box style={{
      width: "100%", height: "100%",
      position: "absolute", top: 0, left: 0,
      zIndex: 11
    }}>
      <ImageBackground
        source={{ uri: checkImage }} 
        style={{ flex: 1, width: '100%' }}
        resizeMode="cover"
      >
        <LiveKitRoom
          style={{ flex: 1, width: "100%", height: "100%" }}
          serverUrl={connectionDetails.wsUrl}
          token={connectionDetails.token}
          connect={connectionDetails.shouldConnect}
          onConnected={()=>{
            setPlaying(1);
            phoneStartTime.current = Date.now();
          }}
          onDisconnected={()=> setPlaying(2) }
          onError={(e) => {
            // setToastMessage({ message: e.message, type: "error" });
            console.error(e);
            setPlaying(2);
          }}
        >
          <Assistant name={character?.name}
            onDisconnect={disconnect}
            playing={playing}
            updateBg={updateBgHandle}
          />
          <RoomAudioRenderer />
          <StartAudio label="Click to enable audio playback" />
        </LiveKitRoom>
      </ImageBackground>
    </Box>
  );
};

export default PhoneScreen;